import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BLOGS, getBlogBySlug } from '../data/blogs/index';
import '../styles/Blogs.css';

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const blog = getBlogBySlug(slug);

  const related = useMemo(() => {
    if (!blog) return BLOGS.slice(0, 3);
    return BLOGS.filter((item) => item.slug !== blog.slug)
      .sort((a, b) => Number(b.featured) - Number(a.featured))
      .slice(0, 3);
  }, [blog]);

  const renderContent = (content = []) =>
    content.map((item, index) => {
      switch (item.type) {
        case 'text':
          return (
            <p key={index} className="blog-text">
              {item.value}
            </p>
          );
        case 'image': {
          const image = typeof item.value === 'string' ? { src: item.value } : item.value;
          return (
            <figure key={index} className="blog-figure">
              <img src={image.src} alt={image.alt || 'Blog illustration'} className="blog-image" />
              {image.caption && <figcaption>{image.caption}</figcaption>}
            </figure>
          );
        }
        case 'code':
          return (
            <pre key={index} className="blog-code">
              <code>{item.value}</code>
            </pre>
          );
        case 'link':
          return (
            <a
              key={index}
              href={item.value.url}
              target="_blank"
              rel="noopener noreferrer"
              className="blog-link"
            >
              {item.value.text}
            </a>
          );
        case 'quote':
          return (
            <blockquote key={index} className="blog-quote">
              {item.value}
            </blockquote>
          );
        case 'list':
          return (
            <ul key={index} className="blog-list">
              {item.value.map((point, pointIndex) => (
                <li key={pointIndex}>{point}</li>
              ))}
            </ul>
          );
        default:
          return null;
      }
    });

  if (!blog) {
    return (
      <section className="blog-detail-page">
        <div className="blog-detail-shell">
          <div className="blog-empty">
            <h2>Blog not found yet.</h2>
            <p>Check back soon, more writing is on the way.</p>
            <button type="button" className="blog-back-inline" onClick={() => navigate('/blogs')}>
              Back to all blogs
            </button>
          </div>
        </div>
      </section>
    );
  }

  const toc = blog.sections.map((section) => ({
    id: slugify(section.title),
    title: section.title,
  }));

  return (
    <section className="blog-detail-page" style={{ '--blog-accent': blog.accent || '#E85D34' }}>
      <div className="blog-detail-shell">
        <div className="blog-detail-nav">
          <button type="button" className="blog-back-link" onClick={() => navigate('/blogs')}>
            ← Back to all blogs
          </button>
        </div>

        <header className="blog-detail-hero">
          <div className="blog-detail-hero-copy">
            <p className="blog-eyebrow">{blog.kicker}</p>
            <h1 className="blog-title">{blog.title}</h1>
            <p className="blog-detail-summary">{blog.summary}</p>
            <div className="blog-detail-meta">
              <span>{blog.date}</span>
              <span>{blog.readTime}</span>
              <span>{blog.tags?.[0] || 'Essay'}</span>
            </div>
            <div className="blog-card-tags blog-card-tags--detail">
              {blog.tags?.map((tag) => (
                <span key={tag} className="blog-tag">{tag}</span>
              ))}
            </div>
          </div>

          <figure className="blog-detail-cover">
            <img src={blog.coverImage} alt={blog.coverAlt} />
          </figure>
        </header>

        <div className="blog-detail-layout">
          <aside className="blog-detail-aside">
            <div className="blog-detail-aside-card">
              <p className="blog-aside-label">Table of contents</p>
              <nav className="blog-toc">
                {toc.map((item) => (
                  <a key={item.id} href={`#${item.id}`} className="blog-toc-link">
                    {item.title}
                  </a>
                ))}
              </nav>
            </div>

            <div className="blog-detail-aside-card blog-detail-aside-card--related">
              <p className="blog-aside-label">More writing</p>
              <div className="blog-related-list">
                {related.map((item) => (
                  <button key={item.slug} type="button" className="blog-related-item" onClick={() => navigate(item.redirectUrl)}>
                    <span>{item.title}</span>
                    <em>{item.readTime}</em>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <article className="blog-detail-body">
            {blog.sections.map((section, index) => {
              const sectionId = slugify(section.title) || `section-${index}`;
              return (
                <section key={index} id={sectionId} className="blog-section">
                  <h2>{section.title}</h2>
                  {renderContent(section.content)}
                </section>
              );
            })}

            {blog.repoUrl && (
              <div className="blog-attempt-cta">
                <a href={blog.repoUrl} target="_blank" rel="noopener noreferrer" className="blog-link">
                  My attempt at this
                </a>
              </div>
            )}
          </article>
        </div>
      </div>
    </section>
  );
};

export default BlogDetail;
